import { Injectable, inject, signal, computed, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject, firstValueFrom, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { WebSocketService } from './websocket.service';
import {
  FaaReport,
  FaaReportStatus,
  CreateFaaReportRequest,
  UpdateFaaReportRequest,
  SubmitFaaReportRequest,
  FaaReportProgressEvent,
  FaaReportCompleteEvent,
  FaaReportFailedEvent,
  FAA_REPORT_TYPE_VALUES,
  FaaReportMissingCountSummary
} from '../models/faa-report.model';

/**
 * API Response wrapper type
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * API List Response - matches backend controller response structure
 * Backend returns data as array directly with sibling pagination fields
 */
interface ApiListResponse<T> {
  success: boolean;
  data?: T[];
  total?: number;
  page?: number;
  limit?: number;
  error?: string;
}

/**
 * FaaReportService
 *
 * Frontend service for FAA compliance report management.
 * Communicates with the backend API and handles real-time updates via WebSocket.
 *
 * Features:
 * - CRUD operations for FAA reports
 * - PDF generation triggering
 * - Real-time progress updates via WebSocket (no polling)
 * - Signal-based reactive state management
 *
 * Reference: /agent-os/specs/2026-01-15-faa-reports/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class FaaReportService implements OnDestroy {
  private http = inject(HttpClient);
  private webSocketService = inject(WebSocketService);
  private apiUrl = environment.apiUrl;
  private destroy$ = new Subject<void>();

  // Signal-based state
  private reportsSignal = signal<FaaReport[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);
  private totalCountSignal = signal<number>(0);
  private currentPageSignal = signal<number>(1);
  private pageSizeSignal = signal<number>(10);

  // Public computed signals
  readonly reports = computed(() => this.reportsSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());
  readonly totalCount = computed(() => this.totalCountSignal());
  readonly currentPage = computed(() => this.currentPageSignal());
  readonly pageSize = computed(() => this.pageSizeSignal());

  // Report type options
  readonly reportTypes = FAA_REPORT_TYPE_VALUES;

  // Computed stats
  readonly generatingReports = computed(() =>
    this.reportsSignal().filter(r => r.status === 'generating')
  );

  readonly readyReports = computed(() =>
    this.reportsSignal().filter(r => r.status === 'ready')
  );

  readonly failedReports = computed(() =>
    this.reportsSignal().filter(r => r.status === 'failed')
  );

  // Track WebSocket subscription state
  private isSubscribed = false;

  constructor() {
    // Subscribe to WebSocket events when connection state changes to authenticated
    this.webSocketService.connectionState$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(state => {
      if (state === 'authenticated' && !this.isSubscribed) {
        this.setupWebSocketSubscriptions();
      } else if (state === 'disconnected') {
        this.isSubscribed = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================
  // WEBSOCKET SUBSCRIPTIONS
  // ============================================

  /**
   * Set up WebSocket subscriptions for real-time FAA report updates.
   * Listens for progress, complete, and failed events.
   * Called when WebSocket connection is authenticated.
   */
  private setupWebSocketSubscriptions(): void {
    if (this.isSubscribed) return;
    this.isSubscribed = true;

    console.log('[FaaReportService] Setting up WebSocket subscriptions for FAA report events');

    // Subscribe to progress events
    this.webSocketService.on<FaaReportProgressEvent>('faa-report:progress')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        console.log('[FaaReportService] Received faa-report:progress:', event);
        this.handleProgressEvent(event);
      });

    // Subscribe to complete events
    this.webSocketService.on<FaaReportCompleteEvent>('faa-report:complete')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        console.log('[FaaReportService] Received faa-report:complete:', event);
        this.handleCompleteEvent(event);
      });

    // Subscribe to failed events
    this.webSocketService.on<FaaReportFailedEvent>('faa-report:failed')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        console.log('[FaaReportService] Received faa-report:failed:', event);
        this.handleFailedEvent(event);
      });

    console.log('[FaaReportService] WebSocket subscriptions established');
  }

  /**
   * Handle progress event from WebSocket
   */
  private handleProgressEvent(event: FaaReportProgressEvent): void {
    this.reportsSignal.update(reports => {
      return reports.map(report => {
        if (report.id === event.reportId) {
          return {
            ...report,
            status: event.status,
            generationProgress: event.progress
          };
        }
        return report;
      });
    });
  }

  /**
   * Handle complete event from WebSocket
   */
  private handleCompleteEvent(event: FaaReportCompleteEvent): void {
    this.reportsSignal.update(reports => {
      return reports.map(report => {
        if (report.id === event.reportId) {
          return {
            ...report,
            status: event.status,
            reportUrl: event.url,
            recordCount: event.recordCount,
            fileSize: event.fileSize,
            generationProgress: 100
          };
        }
        return report;
      });
    });
  }

  /**
   * Handle failed event from WebSocket
   */
  private handleFailedEvent(event: FaaReportFailedEvent): void {
    this.reportsSignal.update(reports => {
      return reports.map(report => {
        if (report.id === event.reportId) {
          return {
            ...report,
            status: event.status,
            errorMessage: event.error,
            generationProgress: 0
          };
        }
        return report;
      });
    });
  }

  // ============================================
  // API METHODS
  // ============================================

  /**
   * List FAA reports for an organization with pagination
   *
   * @param orgId Organization ID
   * @param options Optional query parameters (page, limit, status)
   * @returns Observable with paginated report list
   */
  listReports(
    orgId: string,
    options?: { page?: number; limit?: number; status?: FaaReportStatus }
  ): Observable<ApiListResponse<FaaReport>> {
    let params = new HttpParams();

    if (options?.page) {
      params = params.set('page', options.page.toString());
    }
    if (options?.limit) {
      params = params.set('limit', options.limit.toString());
    }
    if (options?.status) {
      params = params.set('status', options.status);
    }

    return this.http.get<ApiListResponse<FaaReport>>(
      `${this.apiUrl}/organizations/${orgId}/faa-reports`,
      { params }
    );
  }

  /**
   * How many records in a would-be filing have no flight count entered and
   * would be filed as 1 (FAA §F.2.d).
   *
   * Advisory only. Shown before generating so the operator can fix the records
   * first rather than discover the shortfall in a filed PDF — it must never
   * block generation.
   *
   * Returns null on any failure. A warning that cannot be computed must not
   * stop someone filing: an under-count is a compliance problem, being unable
   * to file at all is worse.
   */
  async fetchMissingFlightCounts(
    orgId: string,
    startDate: string,
    endDate: string
  ): Promise<FaaReportMissingCountSummary | null> {
    try {
      const params = new HttpParams()
        .set('startDate', startDate)
        .set('endDate', endDate);

      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data?: FaaReportMissingCountSummary }>(
          `${this.apiUrl}/organizations/${orgId}/faa-reports/missing-flight-counts`,
          { params }
        )
      );

      return response?.success && response.data ? response.data : null;
    } catch (error) {
      console.error('Error fetching missing flight counts:', error);
      return null;
    }
  }

  /**
   * Load reports into signal state
   *
   * @param orgId Organization ID
   * @param options Optional query parameters
   */
  async loadReports(
    orgId: string,
    options?: { page?: number; limit?: number; status?: FaaReportStatus }
  ): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(this.listReports(orgId, options));

      if (response.success && response.data) {
        // Backend returns data as array directly, with pagination as sibling fields
        this.reportsSignal.set(response.data.map(r => this.mapApiToReport(r)));
        this.totalCountSignal.set(response.total ?? 0);
        this.currentPageSignal.set(response.page ?? 1);
        this.pageSizeSignal.set(response.limit ?? 10);
      } else {
        throw new Error(response.error || 'Failed to load reports');
      }
    } catch (error: any) {
      console.error('Error loading FAA reports:', error);
      this.errorSignal.set(error.message || 'Failed to load reports');
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Get a single FAA report by ID
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @returns Observable with single report
   */
  getReport(orgId: string, reportId: string): Observable<ApiResponse<FaaReport>> {
    return this.http.get<ApiResponse<FaaReport>>(
      `${this.apiUrl}/organizations/${orgId}/faa-reports/${reportId}`
    );
  }

  /**
   * Fetch and return a single report
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @returns Promise with report or null
   */
  async fetchReport(orgId: string, reportId: string): Promise<FaaReport | null> {
    try {
      const response = await firstValueFrom(this.getReport(orgId, reportId));

      if (response.success && response.data) {
        return this.mapApiToReport(response.data);
      }
      return null;
    } catch (error: any) {
      console.error('Error fetching FAA report:', error);
      return null;
    }
  }

  /**
   * Create a new FAA report in draft status
   *
   * @param orgId Organization ID
   * @param data Report creation data
   * @returns Observable with created report
   */
  createReport(
    orgId: string,
    data: CreateFaaReportRequest
  ): Observable<ApiResponse<FaaReport>> {
    return this.http.post<ApiResponse<FaaReport>>(
      `${this.apiUrl}/organizations/${orgId}/faa-reports`,
      data
    );
  }

  /**
   * Create report and add to signal state
   *
   * @param orgId Organization ID
   * @param data Report creation data
   * @returns Promise with created report or null
   */
  async createAndAddReport(
    orgId: string,
    data: CreateFaaReportRequest
  ): Promise<FaaReport | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(this.createReport(orgId, data));

      if (response.success && response.data) {
        const newReport = this.mapApiToReport(response.data);
        this.reportsSignal.update(reports => [newReport, ...reports]);
        this.totalCountSignal.update(count => count + 1);
        return newReport;
      } else {
        throw new Error(response.error || 'Failed to create report');
      }
    } catch (error: any) {
      console.error('Error creating FAA report:', error);
      this.errorSignal.set(error.message || 'Failed to create report');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Update an existing FAA report (only if status is draft)
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @param data Update data
   * @returns Observable with updated report
   */
  updateReport(
    orgId: string,
    reportId: string,
    data: UpdateFaaReportRequest
  ): Observable<ApiResponse<FaaReport>> {
    return this.http.put<ApiResponse<FaaReport>>(
      `${this.apiUrl}/organizations/${orgId}/faa-reports/${reportId}`,
      data
    );
  }

  /**
   * Update report and refresh signal state
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @param data Update data
   * @returns Promise with updated report or null
   */
  async updateAndRefreshReport(
    orgId: string,
    reportId: string,
    data: UpdateFaaReportRequest
  ): Promise<FaaReport | null> {
    try {
      const response = await firstValueFrom(this.updateReport(orgId, reportId, data));

      if (response.success && response.data) {
        const updatedReport = this.mapApiToReport(response.data);
        this.reportsSignal.update(reports => {
          return reports.map(r => r.id === reportId ? updatedReport : r);
        });
        return updatedReport;
      } else {
        throw new Error(response.error || 'Failed to update report');
      }
    } catch (error: any) {
      console.error('Error updating FAA report:', error);
      this.errorSignal.set(error.message || 'Failed to update report');
      return null;
    }
  }

  /**
   * Delete an FAA report (soft delete)
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @returns Observable with void response
   */
  deleteReport(orgId: string, reportId: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(
      `${this.apiUrl}/organizations/${orgId}/faa-reports/${reportId}`
    );
  }

  /**
   * Delete report and remove from signal state
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @returns Promise with success boolean
   */
  async deleteAndRemoveReport(orgId: string, reportId: string): Promise<boolean> {
    try {
      // DELETE returns 204 No Content on success, so response body may be null
      const response = await firstValueFrom(this.deleteReport(orgId, reportId));

      // Treat null response (204) or response.success === true as success
      if (response === null || response?.success !== false) {
        this.reportsSignal.update(reports => reports.filter(r => r.id !== reportId));
        this.totalCountSignal.update(count => Math.max(0, count - 1));
        return true;
      } else {
        throw new Error(response.error || 'Failed to delete report');
      }
    } catch (error: any) {
      console.error('Error deleting FAA report:', error);
      this.errorSignal.set(error.message || 'Failed to delete report');
      return false;
    }
  }

  /**
   * Generate PDF for an FAA report (queues background job)
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @returns Observable with response
   */
  generatePdf(orgId: string, reportId: string): Observable<ApiResponse<{ jobId: string }>> {
    return this.http.post<ApiResponse<{ jobId: string }>>(
      `${this.apiUrl}/organizations/${orgId}/faa-reports/${reportId}/generate`,
      {}
    );
  }

  /**
   * Trigger PDF generation and update signal state
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @returns Promise with success boolean
   */
  async triggerPdfGeneration(orgId: string, reportId: string): Promise<boolean> {
    try {
      const response = await firstValueFrom(this.generatePdf(orgId, reportId));

      if (response.success) {
        // Update local state to show generating status
        this.reportsSignal.update(reports => {
          return reports.map(r => {
            if (r.id === reportId) {
              return {
                ...r,
                status: 'generating' as FaaReportStatus,
                generationProgress: 0
              };
            }
            return r;
          });
        });
        return true;
      } else {
        throw new Error(response.error || 'Failed to start generation');
      }
    } catch (error: any) {
      console.error('Error generating FAA report PDF:', error);
      this.errorSignal.set(error.message || 'Failed to start generation');
      return false;
    }
  }

  /**
   * Retry a failed report generation
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @returns Observable with response
   */
  retryReport(orgId: string, reportId: string): Observable<ApiResponse<{ jobId: string }>> {
    return this.http.post<ApiResponse<{ jobId: string }>>(
      `${this.apiUrl}/organizations/${orgId}/faa-reports/${reportId}/retry`,
      {}
    );
  }

  /**
   * Retry failed report and update signal state
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @returns Promise with success boolean
   */
  async retryFailedReport(orgId: string, reportId: string): Promise<boolean> {
    try {
      const response = await firstValueFrom(this.retryReport(orgId, reportId));

      if (response.success) {
        // Update local state to show generating status
        this.reportsSignal.update(reports => {
          return reports.map(r => {
            if (r.id === reportId) {
              return {
                ...r,
                status: 'generating' as FaaReportStatus,
                generationProgress: 0,
                errorMessage: undefined
              };
            }
            return r;
          });
        });
        return true;
      } else {
        throw new Error(response.error || 'Failed to retry generation');
      }
    } catch (error: any) {
      console.error('Error retrying FAA report:', error);
      this.errorSignal.set(error.message || 'Failed to retry generation');
      return false;
    }
  }

  /**
   * Submit a report (simulated - marks as submitted with email)
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @param email Email address to submit to
   * @returns Observable with updated report
   */
  submitReport(
    orgId: string,
    reportId: string,
    email: string
  ): Observable<ApiResponse<FaaReport>> {
    const body: SubmitFaaReportRequest = { email };
    return this.http.post<ApiResponse<FaaReport>>(
      `${this.apiUrl}/organizations/${orgId}/faa-reports/${reportId}/submit`,
      body
    );
  }

  /**
   * Submit report and update signal state
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @param email Email address to submit to
   * @returns Promise with success boolean
   */
  async submitAndUpdateReport(
    orgId: string,
    reportId: string,
    email: string
  ): Promise<boolean> {
    try {
      const response = await firstValueFrom(this.submitReport(orgId, reportId, email));

      if (response.success && response.data) {
        const updatedReport = this.mapApiToReport(response.data);
        this.reportsSignal.update(reports => {
          return reports.map(r => r.id === reportId ? updatedReport : r);
        });
        return true;
      } else {
        throw new Error(response.error || 'Failed to submit report');
      }
    } catch (error: any) {
      console.error('Error submitting FAA report:', error);
      this.errorSignal.set(error.message || 'Failed to submit report');
      return false;
    }
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  /**
   * Map API response to FaaReport interface
   * Handles both snake_case and camelCase field names
   */
  private mapApiToReport(data: any): FaaReport {
    return {
      id: data.id,
      organizationId: data.organizationId ?? data.organization_id,
      name: data.name,
      reportType: data.reportType ?? data.report_type ?? 'complete_faa',
      status: data.status ?? 'draft',
      reportUrl: data.reportUrl ?? data.report_url,
      downloadUrl: data.downloadUrl ?? data.download_url,
      startDate: data.startDate ?? data.start_date,
      endDate: data.endDate ?? data.end_date,
      options: data.options ?? {
        includeFlightLogs: true,
        includeSprayRecords: true,
        includePilotInfo: true,
        includeAircraftInfo: true
      },
      generationProgress: data.generationProgress ?? data.generation_progress ?? 0,
      recordCount: data.recordCount ?? data.record_count,
      fileSize: data.fileSize ?? data.file_size,
      submittedAt: data.submittedAt ?? data.submitted_at,
      submittedTo: data.submittedTo ?? data.submitted_to,
      errorMessage: data.errorMessage ?? data.error_message,
      createdAt: data.createdAt ?? data.created_at,
      updatedAt: data.updatedAt ?? data.updated_at,
      createdBy: data.createdBy ?? data.created_by
    };
  }

  /**
   * Get report by ID from local state
   */
  getReportById(reportId: string): FaaReport | undefined {
    return this.reportsSignal().find(r => r.id === reportId);
  }

  /**
   * Get human-readable status text
   */
  getStatusText(status: FaaReportStatus): string {
    switch (status) {
      case 'draft': return 'Draft';
      case 'generating': return 'Generating...';
      case 'ready': return 'Ready';
      case 'submitted': return 'Submitted';
      case 'failed': return 'Failed';
      default: return status;
    }
  }

  /**
   * Get Bootstrap icon class for status
   */
  getStatusIcon(status: FaaReportStatus): string {
    switch (status) {
      case 'draft': return 'bi-file-earmark';
      case 'generating': return 'bi-arrow-repeat';
      case 'ready': return 'bi-check-circle';
      case 'submitted': return 'bi-send-check';
      case 'failed': return 'bi-exclamation-triangle';
      default: return 'bi-file-earmark';
    }
  }

  /**
   * Get report type label from value
   */
  getReportTypeLabel(value: string): string {
    return this.reportTypes.find(t => t.value === value)?.label || value;
  }

  /**
   * Format date range for display
   */
  formatDateRange(startDate: string, endDate: string): string {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
  }

  /**
   * Check if report can be deleted
   */
  canDelete(report: FaaReport): boolean {
    return report.status !== 'generating';
  }

  /**
   * Check if report can be retried
   */
  canRetry(report: FaaReport): boolean {
    return report.status === 'failed';
  }

  /**
   * Check if report can generate PDF
   */
  canGeneratePdf(report: FaaReport): boolean {
    return report.status === 'draft';
  }

  /**
   * Check if report can be submitted
   */
  canSubmit(report: FaaReport): boolean {
    return report.status === 'ready';
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorSignal.set(null);
  }
}
